/**
 * O que faz: administra conhecimentos revisados e PDFs privados usando a sessão do usuário.
 * Onde usa: aba Base de conhecimento da página de IA.
 * Conexões: Supabase Auth/RLS, Storage e process-clinic-knowledge.
 * Tabelas: clinica_conhecimentos (CRUD filtrado por clínica); storage.objects (PDF privado).
 * Data: 29/09/2026.
 */
import { supabase } from '@/integrations/supabase/client';
import type { SupabaseClient } from '@supabase/supabase-js';
import {
  validarTextoConhecimento, LIMITE_PDF_BYTES, LIMITE_AUDIO_BYTES, type OrigemConhecimento,
} from '../../../supabase/functions/_shared/clinic-knowledge';

// A tabela nova ainda não está no arquivo gerado de tipos; restringimos a adaptação a este serviço,
// preservando a tipagem do client compartilhado e os contratos/validações explícitos da funcionalidade.
const bancoConhecimento = supabase as unknown as SupabaseClient;

export interface ItemConhecimento {
  id: string;
  clinica_id: string;
  titulo: string;
  conteudo: string;
  origem: OrigemConhecimento;
  fonte_nome: string | null;
  fonte_caminho: string | null;
  ativo: boolean;
  created_at: string;
  updated_at: string;
}
export interface RascunhoConhecimento {
  id?: string;
  titulo: string;
  conteudo: string;
  origem: OrigemConhecimento;
  arquivo?: File;
  fonteNome?: string | null;
}

export function mensagemErroConhecimento(erro: unknown): string {
  const falha = erro as { message?: string; code?: string };
  if (falha?.code === '42P01' || falha?.code === 'PGRST205') return 'A base de conhecimento ainda não foi instalada neste ambiente.';
  if (falha?.code === '42501') return 'Você não tem permissão para alterar a base desta clínica.';
  if (falha?.code === '23514') return 'Revise os limites e remova instruções à IA. A base deve conter apenas informações da clínica.';
  // Não mostra mensagens técnicas com detalhes de banco/servidor ao usuário final.
  if (erro instanceof Error && !falha.code) return erro.message;
  if (falha?.message?.includes('60.000') || falha?.message?.includes('50 conteúdos')) return falha.message;
  return 'Não foi possível atualizar a base de conhecimento. Tente novamente.';
}

export async function listarConhecimentos(clinicaId: string): Promise<ItemConhecimento[]> {
  // SELECT campos de exibição/editor da clinica_conhecimentos; clínica é filtro obrigatório além da RLS.
  // Conteúdo é privado: só a clínica do token ou admin autorizado recebe os dados.
  const { data, error } = await bancoConhecimento.from('clinica_conhecimentos')
    .select('id, clinica_id, titulo, conteudo, origem, fonte_nome, fonte_caminho, ativo, created_at, updated_at')
    .eq('clinica_id', clinicaId).order('created_at', { ascending: false }).limit(50);
  if (error) throw error;
  return (data ?? []) as ItemConhecimento[];
}

export async function processarArquivoConhecimento(clinicaId: string, origem: 'pdf' | 'audio', arquivo: File): Promise<{ conteudo: string; suspeito: boolean }> {
  // Limites repetidos no servidor: esta validação economiza transferência/custo e orienta a pessoa.
  if (!arquivo.size || arquivo.size > (origem === 'pdf' ? LIMITE_PDF_BYTES : LIMITE_AUDIO_BYTES)) {
    throw new Error(origem === 'pdf' ? 'Envie um PDF de até 10 MB.' : 'Grave um áudio de até 20 MB.');
  }
  const formulario = new FormData();
  formulario.set('clinicaId', clinicaId);
  formulario.set('origem', origem);
  formulario.set('file', arquivo);
  const { data, error } = await supabase.functions.invoke('process-clinic-knowledge', { body: formulario });
  if (error) {
    // A Function retorna mensagens claras; ler o JSON evita o genérico "non-2xx" do SDK.
    const contexto = (error as unknown as { context?: Response }).context;
    const resposta = contexto ? await contexto.json().catch(() => null) : null;
    throw new Error(resposta?.error || 'Não foi possível processar o arquivo. Tente novamente.');
  }
  if (typeof data?.conteudo !== 'string' || !data.conteudo.trim()) throw new Error('O processamento não retornou texto. Tente novamente.');
  return { conteudo: data.conteudo, suspeito: data.suspeito === true };
}

export async function salvarConhecimento(clinicaId: string, rascunho: RascunhoConhecimento): Promise<void> {
  const textoValidado = validarTextoConhecimento(rascunho.titulo, rascunho.conteudo);
  if (rascunho.id) {
    // UPDATE somente título/conteúdo por id E clínica. Origem/arquivo/autoria não são editáveis.
    const { data, error } = await bancoConhecimento.from('clinica_conhecimentos').update(textoValidado)
      .eq('id', rascunho.id).eq('clinica_id', clinicaId).select('id').single();
    if (error || !data) throw error || new Error('Este conhecimento não está mais disponível. Atualize a página.');
    return;
  }
  const id = crypto.randomUUID();
  const caminho = rascunho.origem === 'pdf' ? `${clinicaId}/${id}.pdf` : null;
  if (caminho) {
    // Um PDF novo precisa do original validado; o caminho é gerado, nunca aceito do texto enviado.
    if (!rascunho.arquivo || rascunho.arquivo.type !== 'application/pdf' || rascunho.arquivo.size > LIMITE_PDF_BYTES) {
      throw new Error('Selecione novamente o PDF antes de salvar.');
    }
    // Storage INSERT: PDF privado, chave clínica/id; RLS verifica clínica, sem sobrescrever arquivos.
    const { error } = await supabase.storage.from('clinica-conhecimento').upload(caminho, rascunho.arquivo, { contentType: 'application/pdf', upsert: false });
    if (error) throw new Error('Não foi possível guardar o PDF. Tente novamente.');
  }
  // INSERT clinica_conhecimentos: só texto revisado, origem, nome e caminho privado.
  // criado_por vem de auth.uid() no banco, e limites/injeção são novamente conferidos no trigger.
  const { error } = await bancoConhecimento.from('clinica_conhecimentos').insert({
    id, clinica_id: clinicaId, ...textoValidado, origem: rascunho.origem,
    fonte_nome: (rascunho.fonteNome || rascunho.arquivo?.name || '').slice(0, 255) || null, fonte_caminho: caminho,
  });
  if (error) {
    // Compensa upload órfão quando o banco recusa a gravação; não remove nenhum PDF existente.
    if (caminho) await supabase.storage.from('clinica-conhecimento').remove([caminho]);
    throw error;
  }
}

export async function definirConhecimentoAtivo(clinicaId: string, id: string, ativo: boolean): Promise<void> {
  // UPDATE ativo por id/clínica; desativar remove o fato da próxima geração sem apagar o registro.
  const { error } = await bancoConhecimento.from('clinica_conhecimentos').update({ ativo })
    .eq('id', id).eq('clinica_id', clinicaId).select('id').single();
  if (error) throw error;
}

export async function excluirConhecimento(clinicaId: string, item: ItemConhecimento): Promise<void> {
  // DELETE id/clínica somente após a confirmação da pessoa na tela; RLS confere acesso novamente.
  const { error } = await bancoConhecimento.from('clinica_conhecimentos').delete().eq('id', item.id).eq('clinica_id', clinicaId).select('id').single();
  if (error) throw error;
  // Só remove PDF da clínica confirmada, mesmo se o objeto da tela estiver obsoleto/alterado.
  if (item.fonte_caminho === `${clinicaId}/${item.id}.pdf`) {
    const { error: erroArquivo } = await supabase.storage.from('clinica-conhecimento').remove([item.fonte_caminho]);
    if (erroArquivo) throw new Error('O conhecimento foi excluído, mas o PDF não pôde ser removido. Avise o administrador.');
  }
}

export async function obterPdfPrivado(clinicaId: string, item: ItemConhecimento): Promise<string> {
  // Link assinado de 60 segundos: mantém o PDF privado e verifica o mesmo caminho da clínica/id.
  if (item.fonte_caminho !== `${clinicaId}/${item.id}.pdf`) throw new Error('PDF indisponível para esta clínica.');
  const { data, error } = await supabase.storage.from('clinica-conhecimento').createSignedUrl(item.fonte_caminho, 60);
  if (error || !data) throw new Error('Não foi possível abrir o PDF. Tente novamente.');
  return data.signedUrl;
}
